/**
 * EmailVerificationPage — handles /verify-email?token=X links.
 * Calls the backend to verify, shows result.
 */
import { useEffect, useState } from "react";
import { API_BASE, setAccessToken, signOutAndWait } from "../api";
import { ChromeTokens } from "../chrome/tokens";
import { PillButton } from "../chrome/PillButton";

interface EmailVerificationPageProps {
  token: string | null;
}

export function EmailVerificationPage({ token }: EmailVerificationPageProps) {
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setMessage("Invalid verification link.");
      return;
    }
    fetch(`${API_BASE}/auth/verify-email`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    })
      .then(async (resp) => {
        if (resp.ok) {
          setStatus("success");
          setMessage("Email verified! ✓");
        } else {
          const body = await resp.json().catch(() => ({}));
          setStatus("error");
          setMessage((body as { detail?: string }).detail || "Verification failed.");
        }
      })
      .catch(() => {
        setStatus("error");
        setMessage("Something went wrong. Try again later.");
      });
  }, [token]);

  return (
    <div style={containerStyle}>
      {status === "loading" && <p style={textStyle}>Verifying...</p>}
      {status === "success" && (
        <>
          <p style={{ ...textStyle, fontSize: 20, fontWeight: 700 }}>{message}</p>
          <p style={{ ...textStyle, fontSize: 14, color: ChromeTokens.color.inkMuted }}>
            You can now log in with your account.
          </p>
          <PillButton
            size="md"
            tone="primary"
            ariaLabel="Log in"
            onClick={() => {
              // Clear any existing session so the login screen shows
              setAccessToken("");
              localStorage.removeItem("access_token");
              try { sessionStorage.setItem("logged_out", "1"); } catch { /* ignore */ }
              void signOutAndWait().then(() => { window.location.href = "/?view=login"; });
            }}
          >
            Log in
          </PillButton>
        </>
      )}
      {status === "error" && (
        <>
          <p style={{ ...textStyle, color: ChromeTokens.color.coral }}>{message}</p>
          <PillButton
            size="sm"
            tone="subtle"
            ariaLabel="Go to login"
            onClick={() => { window.location.href = "/?view=login"; }}
          >
            Go to login
          </PillButton>
        </>
      )}
    </div>
  );
}

const containerStyle: React.CSSProperties = {
  display: "flex", flexDirection: "column", alignItems: "center",
  justifyContent: "center", minHeight: "100vh",
  padding: ChromeTokens.spacing.xl, gap: ChromeTokens.spacing.md,
  background: `linear-gradient(180deg, ${ChromeTokens.color.sky} 0%, ${ChromeTokens.color.base} 100%)`,
};

const textStyle: React.CSSProperties = {
  color: ChromeTokens.color.ink, fontSize: 16, margin: 0, textAlign: "center",
};
